import React from 'react';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { IRulesetSchemaField } from '../../../types/ruleset-types';

interface FieldSelectProps {
  value: string;
  fields: IRulesetSchemaField[];
  onChange: (field: string) => void;
  disabled?: boolean;
}

const FieldSelect: React.FC<FieldSelectProps> = ({
  value,
  fields,
  onChange,
  disabled = false,
}) => {
  // Ensure fields is an array
  const rawFields = Array.isArray(fields) ? fields : [];

  // Use the fields as-is, but ensure we have valid identifiers
  // The API uses 'field_path' as the identifier (e.g., "label", "status")
  const getFieldId = (f: IRulesetSchemaField): string => {
    return (f as any).field_path ?? f.name ?? (f as any).key ?? (f as any).id ?? (f as any).field ?? '';
  };

  const fieldList = rawFields;

  const handleChange = (event: SelectChangeEvent<string>) => {
    // Ensure we never pass undefined - default to empty string
    const newValue = event.target.value ?? '';
    onChange(newValue);
  };

  return (
    <FormControl size="small" sx={{ width: '100%', minWidth: 0 }}>
      <Select
        value={value ?? ''}
        onChange={handleChange}
        disabled={disabled || rawFields.length === 0}
        displayEmpty
        variant="outlined"
        data-cy="select-condition-field"
        sx={{ width: '100%', fontSize: '13px' }}
        renderValue={(selected) => {
          if (!selected) {
            return <em style={{ color: '#999' }}>Select field...</em>;
          }
          const field = fieldList.find((f) => getFieldId(f) === selected);
          return field?.label || selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 300 },
          },
        }}
      >
        <MenuItem value="">
          <em>Select field...</em>
        </MenuItem>
        {fieldList.map((field) => {
          const fieldId = getFieldId(field);
          return (
            <MenuItem key={fieldId || field.label} value={fieldId} data-cy={`field-option-${fieldId}`}>
              {field.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default FieldSelect;
